import {createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode} from 'react';
import analytics from '../config/website-analytics.json';
import deployment from '../config/hetzner-runtime.json';
import {ErrorNotice} from './components';
import type {ErrorDetails} from './lib/types';
import {APP_BASE, Link} from './router';
import {usePublicLocale} from './public-locale';
import './website-analytics.css';

type Consent = 'granted' | 'denied';
export type AnalyticsPage = {path: string; title: string};
const consentKey = 'eightbots.analytics-consent';
const cookiePrefix = 'eightbots';
const origin = new URL(deployment.url).origin;
const measurementId = analytics.measurementId;
const disableKey = `ga-disable-${measurementId}` as const;
const AnalyticsSettingsContext = createContext<(() => void) | null>(null);

declare global {
  interface Window {
    dataLayer?: IArguments[];
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

let tagLoad: Promise<void> | undefined;
function gtag(..._args: unknown[]) {window.dataLayer!.push(arguments);}
function consentFields(allowed: boolean) {
  return {analytics_storage: allowed ? 'granted' : 'denied', ad_storage: 'denied',
    ad_user_data: 'denied', ad_personalization: 'denied'};
}
function disableAnalytics() {
  window[disableKey] = true;
  if (window.dataLayer) gtag('consent', 'update', consentFields(false));
}
function readConsent(): Consent | null {
  const value = localStorage.getItem(consentKey);
  if (value !== null && value !== 'granted' && value !== 'denied') throw new Error('Invalid saved analytics consent choice.');
  return value;
}
function errorDetails(error: unknown): ErrorDetails {
  return error instanceof Error ? {name: error.name, message: error.message, ...(error.stack ? {stack: error.stack} : {})}
    : {name: 'AnalyticsError', message: String(error)};
}
function clearAnalyticsCookies() {
  for (const entry of document.cookie.split(';')) {
    const name = entry.trim().split('=')[0]!;
    if (name === `${cookiePrefix}_ga` || name.startsWith(`${cookiePrefix}_ga_`)) {
      document.cookie = `${name}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
    }
  }
}
function loadTag(page: AnalyticsPage): Promise<void> {
  if (tagLoad) return tagLoad;
  if (!/^G-[A-Z0-9]+$/.test(measurementId)) throw new Error('Invalid Google Analytics measurement ID.');
  if (window.dataLayer) throw new Error('A second Google tag data layer already exists.');
  window.dataLayer = [];
  gtag('consent', 'default', consentFields(false));
  gtag('consent', 'update', consentFields(true));
  gtag('set', {ads_data_redaction: true, url_passthrough: false, page_location: origin + page.path,
    page_title: page.title, page_referrer: ''});
  gtag('js', new Date());
  gtag('config', measurementId, {send_page_view: false, allow_google_signals: false,
    allow_ad_personalization_signals: false, cookie_prefix: cookiePrefix, cookie_domain: 'none',
    cookie_expires: 90 * 24 * 60 * 60, cookie_flags: 'SameSite=Lax;Secure',
    page_location: origin + page.path, page_title: page.title, page_referrer: ''});
  tagLoad = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.referrerPolicy = 'no-referrer';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Google Analytics could not load: ${script.src}`));
    document.head.append(script);
  });
  return tagLoad;
}

export function AnalyticsProvider({page, children}: {page: AnalyticsPage | null; children: ReactNode}) {
  const {t} = usePublicLocale();
  const [consent, setConsent] = useState<Consent | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<ErrorDetails | null>(null);
  const previous = useRef<string | null>(null);
  const allowedHost = window.location.origin === origin;
  useEffect(() => {
    function syncChoice() {
      try {setConsent(readConsent()); setError(null);}
      catch (failure) {disableAnalytics(); setConsent(null); setError(errorDetails(failure));}
    }
    syncChoice();
    function storage(event: StorageEvent) {if (event.key === consentKey || event.key === null) syncChoice();}
    window.addEventListener('storage', storage);
    return () => window.removeEventListener('storage', storage);
  }, []);
  const path = page?.path;
  const title = page?.title;
  useLayoutEffect(() => {
    if (!allowedHost || consent !== 'granted' || !path || !title) {
      disableAnalytics(); previous.current = null; return;
    }
    let current = true;
    window[disableKey] = false;
    void loadTag({path, title}).then(() => {
      if (!current || readConsent() !== 'granted' || previous.current === path) return;
      window[disableKey] = false;
      gtag('consent', 'update', consentFields(true));
      // Only the public route and its fixed label enter Google events.
      const fields = {page_location: origin + path, page_title: title,
        page_referrer: previous.current ? origin + previous.current : ''};
      gtag('set', fields);
      gtag('event', 'page_view', {send_to: measurementId, ...fields});
      previous.current = path;
    }).catch(failure => {if (current) {disableAnalytics(); setError(errorDetails(failure));}});
    return () => {current = false;};
  }, [allowedHost, consent, path, title]);
  function choose(value: Consent) {
    if (value === 'denied') disableAnalytics();
    try {
      localStorage.setItem(consentKey, value);
      if (value === 'denied') clearAnalyticsCookies();
      setConsent(value); setError(null); setOpen(false);
    } catch (failure) {disableAnalytics(); setError(errorDetails(failure));}
  }
  return <AnalyticsSettingsContext.Provider value={() => setOpen(true)}>
    {children}
    {allowedHost && !open && <ErrorNotice error={error}/>}
    {allowedHost && open && <aside className="analytics-consent" aria-label={t('Analytics settings')}>
      <div><p>{t('Allow Google Analytics to measure visits to our public website?')}</p>
        <p>{t('This is optional. You can change your choice in Analytics settings.')} <Link href={`${APP_BASE}/privacy`}>{t('Privacy policy')}</Link></p>
        <ErrorNotice error={error}/></div>
      <div className="analytics-consent-actions"><button type="button" onClick={() => choose('denied')}>{t('Reject analytics')}</button>
        <button type="button" onClick={() => choose('granted')}>{t('Allow analytics')}</button>
        <button type="button" className="text-button" onClick={() => setOpen(false)}>{t('Close')}</button></div>
    </aside>}
  </AnalyticsSettingsContext.Provider>;
}

export function AnalyticsSettingsButton() {
  const open = useContext(AnalyticsSettingsContext);
  const {t} = usePublicLocale();
  if (!open) throw new Error('Analytics settings require AnalyticsProvider.');
  return <button type="button" className="text-button" onClick={open}>{t('Analytics settings')}</button>;
}
